'use client';

import { useState, useEffect } from 'react';
import { Globe, Search, CheckCircle2, ChevronDown } from 'lucide-react';

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

export const COUNTRIES_STATIC = [
    { code: 'DZ', name: 'Algeria', name_ar: 'الجزائر', emoji: '🇩🇿', phone_prefix: '+213' },
    { code: 'SA', name: 'Saudi Arabia', name_ar: 'السعودية', emoji: '🇸🇦', phone_prefix: '+966' },
    { code: 'AE', name: 'UAE', name_ar: 'الإمارات', emoji: '🇦🇪', phone_prefix: '+971' },
    { code: 'EG', name: 'Egypt', name_ar: 'مصر', emoji: '🇪🇬', phone_prefix: '+20' },
    { code: 'MA', name: 'Morocco', name_ar: 'المغرب', emoji: '🇲🇦', phone_prefix: '+212' },
    { code: 'TN', name: 'Tunisia', name_ar: 'تونس', emoji: '🇹🇳', phone_prefix: '+216' },
    { code: 'LY', name: 'Libya', name_ar: 'ليبيا', emoji: '🇱🇾', phone_prefix: '+218' },
    { code: 'QA', name: 'Qatar', name_ar: 'قطر', emoji: '🇶🇦', phone_prefix: '+974' },
    { code: 'KW', name: 'Kuwait', name_ar: 'الكويت', emoji: '🇰🇼', phone_prefix: '+965' },
    { code: 'BH', name: 'Bahrain', name_ar: 'البحرين', emoji: '🇧🇭', phone_prefix: '+973' },
    { code: 'OM', name: 'Oman', name_ar: 'عُمان', emoji: '🇴🇲', phone_prefix: '+968' },
    { code: 'JO', name: 'Jordan', name_ar: 'الأردن', emoji: '🇯🇴', phone_prefix: '+962' },
    { code: 'IQ', name: 'Iraq', name_ar: 'العراق', emoji: '🇮🇶', phone_prefix: '+964' },
    { code: 'FR', name: 'France', name_ar: 'فرنسا', emoji: '🇫🇷', phone_prefix: '+33' },
    { code: 'DE', name: 'Germany', name_ar: 'ألمانيا', emoji: '🇩🇪', phone_prefix: '+49' },
    { code: 'GB', name: 'UK', name_ar: 'المملكة المتحدة', emoji: '🇬🇧', phone_prefix: '+44' },
    { code: 'TR', name: 'Turkey', name_ar: 'تركيا', emoji: '🇹🇷', phone_prefix: '+90' },
    { code: 'OTHER', name: 'Other', name_ar: 'أخرى', emoji: '🌍', phone_prefix: '' },
];

export function useCountry() {
    const [country, setCountryState] = useState<{ code: string; name_ar: string; emoji: string } | null>(null);

    useEffect(() => {
        const saved = localStorage.getItem('s360_country');
        if (saved) {
            try { setCountryState(JSON.parse(saved)); } catch { }
        }
    }, []);

    const setCountry = (c: { code: string; name: string; name_ar: string; emoji: string; phone_prefix: string }) => {
        localStorage.setItem('s360_country', JSON.stringify(c));
        setCountryState(c);
    };

    return { country, setCountry };
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (country: any) => void;
    showAllOption?: boolean;
}

export default function CountrySelectModal({ isOpen, onClose, onSelect, showAllOption = false }: Props) {
    const [countries, setCountries] = useState(COUNTRIES_STATIC);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        fetch(`${API}/api/auth/countries/`)
            .then(r => r.json())
            .then(data => { if (Array.isArray(data) && data.length > 0) setCountries(data); })
            .catch(() => {/* fallback to static list */ });
    }, [isOpen]);

    if (!isOpen) return null;

    const filtered = countries.filter(c =>
        c.name?.toLowerCase().includes(search.toLowerCase()) ||
        (c.name_ar || '').includes(search) ||
        (c.code || '').toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (c: any) => {
        onSelect(c);
        onClose();
        setSearch('');
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div
                className="bg-white rounded-3xl shadow-2xl w-full max-w-sm max-h-[80vh] flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 pb-4 border-b border-slate-100">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2.5 bg-blue-50 rounded-2xl">
                            <Globe className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-800">اختر دولتك</h2>
                            <p className="text-xs text-slate-400">سيتم عرض الإعلانات والسيارات المتعلقة بدولتك</p>
                        </div>
                    </div>
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            autoFocus
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="ابحث عن دولتك..."
                            className="w-full pr-10 pl-4 py-2.5 rounded-2xl bg-slate-50 text-sm text-slate-800 placeholder-slate-400 border border-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                {/* Country List */}
                <div className="overflow-y-auto flex-1 p-3">
                    {showAllOption && (
                        <button
                            onClick={() => handleSelect({ code: '', name: 'All Countries', name_ar: 'كل الدول', emoji: '🌍', phone_prefix: '' })}
                            className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-slate-50 transition text-right mb-1"
                        >
                            <span className="text-2xl">🌍</span>
                            <span className="flex-1 font-semibold text-slate-700">كل الدول</span>
                        </button>
                    )}
                    {filtered.map(c => (
                        <button
                            key={c.code}
                            onClick={() => handleSelect(c)}
                            className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-blue-50 transition text-right group"
                        >
                            <span className="text-2xl">{c.emoji}</span>
                            <div className="flex-1 text-right">
                                <p className="font-bold text-slate-800 text-sm">{c.name_ar}</p>
                                <p className="text-xs text-slate-400">{c.name} {c.phone_prefix && `· ${c.phone_prefix}`}</p>
                            </div>
                            <CheckCircle2 className="h-4 w-4 text-blue-500 opacity-0 group-hover:opacity-100 transition" />
                        </button>
                    ))}
                    {filtered.length === 0 && (
                        <p className="text-center text-sm text-slate-400 py-8">لا توجد نتائج</p>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Small badge for top-bar country indicator */
export function CountryBadge({ onClick }: { onClick: () => void }) {
    const { country } = useCountry();
    if (!country) return null;
    return (
        <button
            onClick={onClick}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 hover:bg-blue-100 border border-blue-100 text-xs font-bold text-blue-700 transition"
        >
            <span>{country.emoji}</span>
            <span className="hidden sm:block">{country.name_ar}</span>
            <ChevronDown className="h-3 w-3" />
        </button>
    );
}
